/**
 * 客服统一调用模块（更换客服平台时唯一需要修改的文件）。
 *
 * 调用契约：
 * - Vue 组件：`@pointerdown="preloadCustomerService"` + `@click="openCustomerService"`（行为）+ `class="kf-consult-btn"`（仅样式）
 * - 富文本（v-html）内容：`popup-customer-service` 类名，由本模块的事件委托兜底；
 *   `popupKf53` 是历史遗留类名，仅作旧内容兼容，旧内容清理完成后可移除该选择器。
 *
 * SSR 安全：模块顶层不访问 window/document；服务端不会调用 openCustomerService
 * （service 未初始化时直接返回）。
 */

const KF53_READY_TIMEOUT_MS = 30000;

/** 工厂：host/document/notify 可注入，便于单元测试。 */
export function createCustomerService({ host, document, notify, readyTimeoutMs = KF53_READY_TIMEOUT_MS }) {
    let scriptPromise = null;
    let openPending = false;
    let statusDismissed = false;

    const notifyUser = notify || showStatusPanel;

    function readUrl() {
        return host.__INITIAL_SITE_CONFIG__?.config?.kf53Url;
    }

    /** 主库存在不代表会话终端已经就绪。 */
    function isReady() {
        const sdk = host.$53;
        return !!sdk && (sdk.isOnload === 'pc' || sdk.isOnload === 'mobile') &&
            typeof sdk.createApi === 'function' && !!sdk.openurl;
    }

    /** 按下预加载和点击共用一个加载任务，失败后允许重新加载。 */
    function loadScript() {
        if (isReady()) return Promise.resolve();
        if (scriptPromise) return scriptPromise;

        const kf53Url = readUrl();
        if (!kf53Url) return Promise.reject(new Error('53KF URL is missing from site config.'));

        scriptPromise = new Promise((resolve, reject) => {
            let script = document.querySelector('script[src="' + kf53Url + '"]');
            const shouldAppend = !script;
            if (!script) {
                script = document.createElement('script');
                script.src = kf53Url;
                script.async = true;
            }

            let settled = false;
            function finish(error) {
                if (settled) return;
                settled = true;
                clearInterval(readyTimer);
                clearTimeout(timeoutTimer);
                script.removeEventListener('error', onError);
                if (error) {
                    // 超时后脚本仍可能执行，保留它避免重复初始化；网络错误才移除。
                    if (!error.timeout) script.remove();
                    reject(error);
                } else {
                    resolve();
                }
            }
            function onError() {
                finish(new Error('Failed to load 53KF script.'));
            }

            // 同时等待主库和会话子资源，整个过程都有超时反馈。
            const readyTimer = setInterval(() => {
                if (isReady()) finish();
            }, 50);
            const timeoutTimer = setTimeout(() => {
                const error = new Error('53KF SDK readiness timed out.');
                error.timeout = true;
                finish(error);
            }, readyTimeoutMs);
            script.addEventListener('error', onError);
            if (shouldAppend) document.body.appendChild(script);
        }).catch(error => {
            scriptPromise = null;
            throw error;
        });
        return scriptPromise;
    }

    /** 终端完成初始化后才调用，避免把 SDK 排队误当成开窗成功。 */
    function triggerChat() {
        if (!isReady()) return false;
        const originalOpen = host.open;
        let blocked = false;
        try {
            host.open = function (...args) {
                const chatWindow = originalOpen.apply(host, args);
                if (!chatWindow) blocked = true;
                return chatWindow;
            };
            const api = host.$53.createApi();
            api.push('cmd', 'kfclient');
            api.push('type', 'new');
            const result = api.query();
            return !blocked && result?.code === '201';
        } catch (error) {
            console.warn('53KF open:', error);
            return false;
        } finally {
            host.open = originalOpen;
        }
    }

    /** 加载、失败和再次打开的反馈入口，键盘和触屏均可操作（默认浮层实现）。 */
    function showStatusPanel(message, actionLabel, action) {
        if (statusDismissed) return;
        let panel = document.getElementById('kf53-status');
        if (!panel) {
            panel = document.createElement('div');
            panel.id = 'kf53-status';
            panel.style.cssText = 'position:fixed;right:20px;bottom:24px;z-index:2147483647;max-width:calc(100vw - 40px);padding:16px;background:#fff;color:#222;border:1px solid #ddd;border-radius:8px;box-shadow:0 4px 20px #0002;font:14px/1.6 sans-serif';
            document.body.appendChild(panel);
        }
        panel.replaceChildren();
        const text = document.createElement('div');
        text.setAttribute('role', 'status');
        text.textContent = message;
        panel.appendChild(text);
        if (action) {
            const button = document.createElement('button');
            button.type = 'button';
            button.textContent = actionLabel;
            button.style.cssText = 'margin:10px 12px 0 0;padding:6px 12px;cursor:pointer';
            button.onclick = action;
            panel.appendChild(button);
        }
        const close = document.createElement('button');
        close.type = 'button';
        close.textContent = '关闭提示';
        close.onclick = function () { statusDismissed = true; panel.remove(); };
        panel.appendChild(close);
    }

    /** 就绪后尝试开窗；浏览器拦截时保留手动重试入口。 */
    function submitOpen() {
        if (triggerChat()) {
            document.getElementById('kf53-status')?.remove();
        } else {
            notifyUser('客服窗口未能打开，请允许本站弹出窗口后重试。', '重新打开', open);
        }
    }

    function open() {
        statusDismissed = false;
        if (openPending) {
            notifyUser('正在连接客服，请稍候…');
            return Promise.resolve(false);
        }
        if (isReady()) {
            submitOpen();
            return Promise.resolve(true);
        }

        openPending = true;
        notifyUser('正在连接客服，请稍候…');
        return loadScript().then(() => {
            if (!statusDismissed) submitOpen();
            return true;
        }).catch(error => {
            console.error('53KF:', error);
            if (error.timeout) {
                notifyUser('客服连接较慢或初始化失败，请重新加载页面后再试。', '重新加载页面', () => host.location.reload());
            } else {
                notifyUser('客服连接失败，请检查网络后重试。', '重试', open);
            }
            return false;
        }).finally(() => {
            openPending = false;
        });
    }

    /** 百度统计上报：仅在正式环境（域名不含 rc）且埋点齐全时发送。 */
    function trackBurial(dataset) {
        const buriedName = dataset?.buriedname;
        const buriedValue = dataset?.buriedvalue;
        if (!buriedName || !buriedValue) return;
        if (String(host.location?.hostname || '').includes('rc')) return;
        const tracker = host._hmt;
        if (!tracker) return;
        try {
            tracker.push(['_trackEvent', buriedName, 'click', buriedValue]);
        } catch (error) {
            console.warn('Baidu Analytics tracking failed:', error);
        }
    }

    /** 预连接客服域名并预取脚本（不执行），提升首次点击速度。 */
    function preloadLinks() {
        try {
            const kf53Url = readUrl();
            if (!kf53Url) return;
            try {
                const origin = new URL(kf53Url).origin;
                const preconnect = document.createElement('link');
                preconnect.rel = 'preconnect';
                preconnect.href = origin;
                document.head.appendChild(preconnect);
                const dnsPrefetch = document.createElement('link');
                dnsPrefetch.rel = 'dns-prefetch';
                dnsPrefetch.href = origin;
                document.head.appendChild(dnsPrefetch);
            } catch (e) { /* URL 不可解析时忽略 */ }
            // 仅预取脚本，不执行，防止第三方脚本初始化时自动弹窗。
            // 与后续未设置 crossOrigin 的普通 script 保持相同请求模式，确保预加载可复用。
            if (!document.querySelector('link[rel="preload"][href="' + kf53Url + '"]')) {
                const preload = document.createElement('link');
                preload.rel = 'preload';
                preload.as = 'script';
                preload.href = kf53Url;
                document.head.appendChild(preload);
            }
        } catch (e) {
            console.warn('preloadLinks error:', e);
        }
    }

    /** 带埋点的开窗入口（组件 @click 与富文本委托共用）。 */
    function openFromElement(dataset) {
        trackBurial(dataset);
        return open();
    }

    return { loadScript, open, openFromElement, preloadLinks, isReady };
}

let service = null;

/** 应用启动时调用一次（仅客户端入口）；notify 可注入自定义提示实现。 */
export function initializeCustomerService(notify) {
    if (service) return;
    service = createCustomerService({ host: window, document, notify });

    // 兼容旧富文本（v-html）里的咨询按钮：原生事件委托，不依赖 jQuery。
    document.addEventListener('click', event => {
        const target = event.target.closest?.('.popup-customer-service, .popupKf53');
        if (!target) return;
        event.preventDefault();
        event.stopPropagation();
        service.openFromElement(target.dataset);
    });

    // 用户按下时即开始加载脚本，正式点击触发时通常已就绪。
    document.addEventListener('pointerdown', event => {
        if (event.target.closest?.('.popup-customer-service, .popupKf53')) {
            preloadCustomerService();
        }
    });

    // 页面空闲后预连接/预取客服脚本。
    const preload = () => service.preloadLinks();
    if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(preload, { timeout: 500 });
    } else {
        setTimeout(preload, 200);
    }
}

/** Vue 组件唯一的客服调用入口；以后更换平台，只需修改本模块。 */
export function openCustomerService(event) {
    if (!service) return Promise.resolve(false);
    event?.preventDefault?.();
    const target = event?.currentTarget;
    return service.openFromElement(target?.dataset);
}

/** Vue 组件按下时预加载；只初始化 SDK，不提交开窗或埋点。 */
export function preloadCustomerService() {
    if (!service) return Promise.resolve();
    return service.loadScript().catch(error => console.warn('53KF preload:', error));
}
